import type {
  MemoryBankNoteMetadata,
  MemoryBankSupportedEntityTypes,
} from '@erp/rpc';
import { T } from 'gt-react';
import { MemoryBankEntityPill } from './MemoryBankEntityPill';
import { MemoryBankNoteMetadatum } from './MemoryBankNoteMetadatum';

interface NoteContextPanelProps {
  show: boolean;
  metadata: Partial<MemoryBankNoteMetadata>;
  entityType?: MemoryBankSupportedEntityTypes;
  entityId?: string;
  allowMetadataRemoval: boolean;
  onRemoveMetadata: (key: string) => void;
}

export function NoteContextPanel({
  show,
  metadata,
  entityType,
  entityId,
  allowMetadataRemoval,
  onRemoveMetadata,
}: NoteContextPanelProps) {
  const hasMetadata = Object.keys(metadata).length > 0;
  const hasEntityInfo = entityType !== undefined && entityId !== undefined;
  if (!show)
    return hasEntityInfo ? (
      <MemoryBankEntityPill entityType={entityType} entityId={entityId} />
    ) : null;
  if (!hasMetadata && !hasEntityInfo) return null;

  return (
    <div className="mt-3 p-2.5 rounded-md bg-gray-50 border border-gray-200">
      <div className="text-xs font-medium text-gray-700 mb-1.5">
        <T context="label for the context metadata section when creating a note">
          Note context
        </T>
      </div>
      {hasEntityInfo && (
        <div className="mb-2">
          <div className="text-xs text-muted-foreground mb-1">
            <T context="label indicating which entity the note will be attached to">
              Adding note to:
            </T>
          </div>
          <MemoryBankEntityPill entityType={entityType} entityId={entityId} />
        </div>
      )}
      {hasMetadata && (
        <div>
          <div className="text-xs text-muted-foreground mb-1">
            <T context="label above additional metadata context when creating a note">
              Additional context:
            </T>
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            {Object.entries(metadata).map(([key, value]) => (
              <MemoryBankNoteMetadatum
                name={key}
                value={
                  value as MemoryBankNoteMetadata[keyof MemoryBankNoteMetadata]
                }
                key={key}
                onRemove={
                  allowMetadataRemoval ? () => onRemoveMetadata(key) : undefined
                }
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
